import { useLayoutEffect, useRef } from 'react';
import { IconX } from '@tabler/icons-react';

import { useText } from '../features/clients/words';
import { MaskaTelefona } from '../features/hodba/MaskaTelefona';
import { ZnachokKanala } from '../features/obrashcheniya/components/ZnachokKanala';
import { fetchHronikaObrashcheniya, type Razbor as RazborDannye } from '../features/obrashcheniya/api';
import { LentaHroniki } from '../features/obrashcheniya/components/LentaHroniki';

/**
 * МИНИ-КАРТОЧКА КЛИЕНТКИ ВЫДВИЖНОЙ ПАНЕЛЬЮ (`s608`, Ф3; решение владельца Б, 18.09).
 *
 * ⛔ ИЗ ЛАЙТА НЕ ВЫХОДИМ. Владелец сказал прямо: «в админку за историей не
 * гоняем». Панель выезжает поверх переписки и закрывается обратно в неё —
 * разговор при этом не теряется и не перезагружается.
 *
 * ⛔ НИ ОДНОГО СВОЕГО ЗАПРОСА. Кто это, телефон, визиты, хроника, мессенджеры
 * и связанные карточки приезжают ТЕМ ЖЕ ответом разбора, что и лента (трубы
 * `s607`). Спроси панель сервер сама — на экране завелась бы вторая правда о
 * том же человеке, и разошлись бы они молча (`s320`, девять источников одной
 * величины).
 *
 * ⛔ ТЕЛЕФОН — ТОЛЬКО ПОД МАСКОЙ СИСТЕМЫ (`MaskaTelefona`): правило выдачи
 * стоит на общей трубе, и обходить его ради «маленького экрана» нельзя.
 */
interface Props {
  dannye: RazborDannye;
  onZakryt: () => void;
}

export function KartochkaKlientki({ dannye, onZakryt }: Props) {
  const text = useText();
  const { shapka, hronika, eyo_messendzhery: messendzhery, svyazannye } = dannye;
  const okno = useRef<HTMLDivElement>(null);

  // `s619`: хроника открывается на свежем, как в карточке и в окне записи.
  useLayoutEffect(() => {
    if (okno.current !== null) okno.current.scrollTop = okno.current.scrollHeight;
  }, [shapka.customer_id]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Затемнение — оно же выход: нажатие мимо панели закрывает её. */}
      <button
        type="button"
        aria-label={text('obrashcheniya.otmena')}
        onClick={onZakryt}
        className="absolute inset-0 cursor-default bg-black/40"
      />

      <aside className="relative flex h-full w-full max-w-[420px] flex-col overflow-hidden bg-ops-surface shadow-ops-pop">
        <header className="flex shrink-0 items-start gap-2 border-b border-ops-border px-3 py-2.5">
          <div className="min-w-0 flex-1">
            <div className="truncate text-ops-title font-semibold text-ops-text">
              {shapka.customer_display ?? text('obrashcheniya.kto_eto')}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-ops-micro text-ops-text-secondary">
              {shapka.customer_id !== null && (
                <MaskaTelefona
                  masked={shapka.telefon}
                  entityType="customers"
                  entityId={shapka.customer_id}
                  bezZvonka
                />
              )}
              <span>
                {shapka.vizitov && shapka.vizitov > 0
                  ? text('obrashcheniya.vizitov', { число: shapka.vizitov })
                  : text('obrashcheniya.ni_razu')}
              </span>
              {/*
                ⛔ Дата последнего визита в панели показывается ЦЕЛИКОМ. В шапке
                разбора её прячут на 390 намеренно (`s605`): там она не влезала
                и читалась обрезанной. Здесь ширина своя, и прятать её значило
                бы лишить панель того, ради чего её открыли.
              */}
              {shapka.posledniy_vizit !== null && <span>{shapka.posledniy_vizit}</span>}
            </div>
          </div>

          <button
            type="button"
            onClick={onZakryt}
            aria-label={text('obrashcheniya.otmena')}
            className="shrink-0 rounded-ops-pill p-1 text-ops-text-secondary hover:bg-ops-surface-2 hover:text-ops-primary"
          >
            <IconX size={20} />
          </button>
        </header>

        {messendzhery.length > 0 && (
          <div className="flex shrink-0 flex-wrap gap-1.5 border-b border-ops-border px-3 py-2">
            {messendzhery.map((m) => (
              /*
                ⛔ Пилюли здесь ПОКАЗЫВАЮТ, а не переключают разговор. Ход
                «написать сюда» живёт в разборе и остаётся там один (§31.8):
                заведи его и в панели — и одно действие получило бы два места.
              */
              <span
                key={`${m.kind}-${m.channel_id}`}
                title={m.adres}
                className="flex items-center gap-1.5 rounded-ops-pill border border-ops-border px-2 py-1 text-ops-micro text-ops-text-secondary"
              >
                <ZnachokKanala kind={m.kind} color={shapka.filial.color} razmer={14} />
                {m.kind_display}
              </span>
            ))}
          </div>
        )}

        {svyazannye.length > 0 && (
          <div className="shrink-0 border-b border-ops-border px-3 py-2 text-ops-micro text-ops-text-secondary">
            {text('obrashcheniya.svyazannye')}: {svyazannye.map((s) => s.imya).join(', ')}
          </div>
        )}

        <div ref={okno} className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
          <div className="mb-2 text-ops-label font-semibold text-ops-text">
            {text('obrashcheniya.vsya_hronika')}
          </div>

          {/*
            ⛔ `s619`: та же лента, что в разборе и в карточке клиентки, —
            импортом, а не своей копией строк. Порции, пометка канала и
            вложения решены там один раз.
          */}
          <LentaHroniki
            nachalo={hronika}
            color={shapka.filial.color}
            klyuch={shapka.customer_id}
            zagruzit={(k) => fetchHronikaObrashcheniya(dannye.id, k)}
          />
        </div>
      </aside>
    </div>
  );
}
